import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Users as UsersIcon, Mail, Shield, Building2, MoreVertical, Key, Send, Plus, UserPlus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Skeleton } from '@/components/ui/skeleton';

interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  created_at: string;
  role?: string;
  client_name?: string;
  client_id?: string;
}

interface Client {
  id: string;
  name: string;
}

export default function Users() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFullName, setInviteFullName] = useState('');
  const [inviteRole, setInviteRole] = useState<'user' | 'super_user'>('user');
  const [inviteClientId, setInviteClientId] = useState('');
  const [inviteNewClientName, setInviteNewClientName] = useState('');
  
  const { role, clientId: currentUserClientId } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profilesRes, rolesRes, clientUsersRes, clientsRes] = await Promise.all([
        supabase.from('profiles').select('*').order('created_at', { ascending: false }),
        supabase.from('user_roles').select('*'),
        supabase.from('client_users').select('*, clients(name)'),
        supabase.from('clients').select('id, name').eq('is_active', true),
      ]);

      if (profilesRes.error) throw profilesRes.error;

      const usersWithRoles = (profilesRes.data || []).map(profile => {
        const userRole = (rolesRes.data || []).find(r => r.user_id === profile.id);
        const clientUser = (clientUsersRes.data || []).find(cu => cu.user_id === profile.id);
        return {
          ...profile,
          role: userRole?.role || 'user',
          client_name: (clientUser?.clients as any)?.name,
          client_id: clientUser?.client_id,
        };
      });

      // Filter users based on role
      let filteredUsers = usersWithRoles;
      if (role === 'super_user') {
        filteredUsers = usersWithRoles.filter(u => u.client_id === currentUserClientId);
      }

      setUsers(filteredUsers);
      setClients(clientsRes.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId: string, newRole: 'admin' | 'super_user' | 'user') => {
    try {
      const { data: existingRole } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (existingRole) {
        const { error } = await supabase
          .from('user_roles')
          .update({ role: newRole })
          .eq('user_id', userId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_roles')
          .insert([{ user_id: userId, role: newRole }]);
        if (error) throw error;
      }

      setUsers(users.map(u => 
        u.id === userId ? { ...u, role: newRole } : u
      ));
      toast({ title: "Ruolo aggiornato" });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: error.message,
      });
    }
  };

  const assignToClient = async (userId: string, clientId: string) => {
    try {
      await supabase
        .from('client_users')
        .delete()
        .eq('user_id', userId);

      if (clientId) {
        const { error } = await supabase
          .from('client_users')
          .insert({ user_id: userId, client_id: clientId });
        if (error) throw error;
      }

      const client = clients.find(c => c.id === clientId);
      setUsers(users.map(u => 
        u.id === userId ? { ...u, client_name: client?.name, client_id: clientId } : u
      ));
      toast({ title: "Assegnazione aggiornata" });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: error.message,
      });
    }
  };

  const sendPasswordResetEmail = async (user: UserProfile) => {
    setActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-user-password', {
        body: { action: 'reset_password_email', targetUserId: user.id },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      toast({ title: "Email inviata", description: `Email di reset inviata a ${user.email}` });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: error.message,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const setUserPassword = async () => {
    if (!selectedUser || !newPassword) return;
    
    setActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-user-password', {
        body: { action: 'set_password', targetUserId: selectedUser.id, newPassword },
      });

      if (error) throw error;
      if (data.error) {
        // Check if it's a compromised password error (422)
        if (data.error.includes('compromessa') || data.error.includes('comune')) {
          toast({
            variant: "destructive",
            title: "Errore 422 - Password compromessa",
            description: "La password inserita risulta in database di credenziali violate. Scegli una password diversa e più sicura.",
          });
          return;
        }
        throw new Error(data.error);
      }

      toast({ title: "Password aggiornata", description: `Password impostata per ${selectedUser.email}` });
      setPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: error.message,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const inviteUser = async () => {
    if (!inviteEmail) return;

    const wantsNewClient = inviteClientId === '__new__';
    if (wantsNewClient && !inviteNewClientName.trim()) {
      toast({
        variant: "destructive",
        title: "Nome cliente mancante",
        description: "Inserisci il nome del nuovo cliente dedicato",
      });
      return;
    }

    setActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: { 
          email: inviteEmail, 
          fullName: inviteFullName,
          role: role === 'admin' ? inviteRole : 'user',
          clientId: !wantsNewClient && role === 'admin' ? inviteClientId : undefined,
          newClientName: wantsNewClient ? inviteNewClientName.trim() : undefined,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      toast({ 
        title: "Utente invitato", 
        description: `Invito inviato a ${inviteEmail}` 
      });
      
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteFullName('');
      setInviteRole('user');
      setInviteClientId('');
      setInviteNewClientName('');
      
      // Refresh the users list
      fetchData();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: error.message,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const openPasswordDialog = (user: UserProfile) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const getRoleBadgeVariant = (userRole: string) => {
    switch (userRole) {
      case 'admin': return 'destructive';
      case 'super_user': return 'default';
      default: return 'secondary';
    }
  };

  const getRoleLabel = (userRole: string) => {
    switch (userRole) {
      case 'admin': return 'Admin';
      case 'super_user': return 'Super User';
      default: return 'Utente';
    }
  };

  // Determine what actions the current user can perform
  const canManageRoles = role === 'admin';
  const canManagePasswords = role === 'admin' || role === 'super_user';
  const canAssignClients = role === 'admin';
  const canInviteUsers = role === 'admin' || role === 'super_user';

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Utenti</h1>
            <p className="text-muted-foreground mt-1">
              {role === 'super_user' 
                ? 'Gestisci gli utenti del tuo team'
                : 'Gestisci gli utenti e le loro autorizzazioni'}
            </p>
          </div>
          {canInviteUsers && (
            <Button onClick={() => setInviteDialogOpen(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              Invita Utente
            </Button>
          )}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        ) : users.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-16 text-center">
              <UsersIcon className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                Nessun utente
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Gli utenti registrati appariranno qui
              </p>
              {canInviteUsers && (
                <Button onClick={() => setInviteDialogOpen(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invita il primo utente
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <div className="divide-y">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary">
                          {user.email.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{user.full_name || user.email}</p>
                          <Badge variant={getRoleBadgeVariant(user.role || 'user')}>
                            {getRoleLabel(user.role || 'user')}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {user.email}
                          </span>
                          {user.client_name && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {user.client_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {canManageRoles && (
                        <Select 
                          value={user.role || 'user'} 
                          onValueChange={(v: 'admin' | 'super_user' | 'user') => updateUserRole(user.id, v)}
                        >
                          <SelectTrigger className="w-[140px]">
                            <Shield className="w-4 h-4 mr-2" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">Utente</SelectItem>
                            <SelectItem value="super_user">Super User</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      )}

                      {canAssignClients && (
                        <Select 
                          value={clients.find(c => c.name === user.client_name)?.id || 'none'} 
                          onValueChange={(v) => assignToClient(user.id, v === 'none' ? '' : v)}
                        >
                          <SelectTrigger className="w-[180px]">
                            <Building2 className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Nessun cliente" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nessun cliente</SelectItem>
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {canManagePasswords && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => sendPasswordResetEmail(user)}
                              disabled={actionLoading}
                            >
                              <Send className="w-4 h-4 mr-2" />
                              Invia email reset password
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openPasswordDialog(user)}>
                              <Key className="w-4 h-4 mr-2" />
                              Imposta nuova password
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Imposta nuova password</DialogTitle>
            <DialogDescription>
              Imposta una nuova password per {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Nuova password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimo 6 caratteri"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={setUserPassword} 
              disabled={actionLoading || newPassword.length < 6}
            >
              {actionLoading ? 'Salvataggio...' : 'Salva password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invita nuovo utente</DialogTitle>
            <DialogDescription>
              Invia un invito via email per aggiungere un nuovo utente
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="inviteEmail">Email *</Label>
              <Input
                id="inviteEmail"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="utente@esempio.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inviteFullName">Nome completo</Label>
              <Input
                id="inviteFullName"
                value={inviteFullName}
                onChange={(e) => setInviteFullName(e.target.value)}
                placeholder="Mario Rossi"
              />
            </div>
            
            {role === 'admin' && (
              <>
                <div className="space-y-2">
                  <Label>Ruolo</Label>
                  <Select 
                    value={inviteRole} 
                    onValueChange={(v: 'user' | 'super_user') => setInviteRole(v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Utente</SelectItem>
                      <SelectItem value="super_user">Super User</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Assegna a cliente</Label>
                  <Select 
                    value={inviteClientId || 'none'} 
                    onValueChange={(v) => setInviteClientId(v === 'none' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessun cliente</SelectItem>
                      <SelectItem value="__new__">➕ Nuovo cliente dedicato</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Scegli un cliente esistente per condividere ricerche e bandi, oppure "Nuovo cliente dedicato" per dare all'utente dati completamente separati.
                  </p>
                </div>
              </>
            )}

            {role === 'super_user' && (
              <div className="space-y-2">
                <Label>Dati dell'utente</Label>
                <Select
                  value={inviteClientId === '__new__' ? '__new__' : 'team'}
                  onValueChange={(v) => setInviteClientId(v === '__new__' ? '__new__' : '')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="team">Aggiungi al mio team (dati condivisi)</SelectItem>
                    <SelectItem value="__new__">➕ Nuovo cliente dedicato (dati separati)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {inviteClientId === '__new__' && (
              <div className="space-y-2">
                <Label htmlFor="inviteNewClientName">Nome del nuovo cliente *</Label>
                <Input
                  id="inviteNewClientName"
                  value={inviteNewClientName}
                  onChange={(e) => setInviteNewClientName(e.target.value)}
                  placeholder="Es. Mario Rossi SRL"
                  maxLength={120}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={inviteUser} 
              disabled={actionLoading || !inviteEmail}
            >
              {actionLoading ? 'Invio...' : 'Invia invito'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
